import axios from 'axios';
import { API_BASE_URL } from '../config/apiConfig';

// Keys used to persist auth state in localStorage.
// Kept identical to the RN app so a shared backend contract works seamlessly.
export const AUTH_KEYS = {
  token: 'authToken',
  refreshToken: 'refreshToken',
  user: 'user',
};

// Safe localStorage helpers (guarded for non-browser / test environments).
export const storage = {
  get(key) {
    try {
      if (typeof window === 'undefined') return null;
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      if (typeof window === 'undefined') return;
      window.localStorage.setItem(key, value);
    } catch {
      /* ignore write errors (private-mode etc.) */
    }
  },
  remove(key) {
    try {
      if (typeof window === 'undefined') return;
      window.localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  },
};

const apiClient = axios.create({
  baseURL: API_BASE_URL,
});

// Attach bearer token and the ngrok bypass header on every request
// (ngrok header is harmless against the production API).
apiClient.interceptors.request.use(
  (config) => {
    const token = storage.get(AUTH_KEYS.token);
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers = config.headers || {};
    config.headers['ngrok-skip-browser-warning'] = 'true';
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle token invalidation globally. Matches the behaviour of the RN app:
// when the server returns 401 with a token-related message, clear storage
// and throw a specially tagged error so callers can redirect to login.
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error?.response?.status === 401) {
      const errorMessage = error.response?.data?.message || '';
      if (
        errorMessage.includes('token') ||
        errorMessage.includes('Invalid') ||
        errorMessage.includes('expired')
      ) {
        storage.remove(AUTH_KEYS.token);
        storage.remove(AUTH_KEYS.refreshToken);
        storage.remove(AUTH_KEYS.user);

        const authError = new Error('Authentication failed');
        authError.isAuthError = true;
        authError.originalError = error;
        return Promise.reject(authError);
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;
